import { useEffect, useState } from 'react';
import api from '../../api/axios';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter from '../../components/StatusFilter';
import RequestComments from '../../components/RequestComments';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import RequestFormModal from '../../components/RequestFormModal';

const DECISION_STYLE = {
  approved: { dot: '#4f6ef7', bg: '#eef2ff', label: 'Approved' },
  rejected: { dot: '#dc2626', bg: '#fee2e2', label: 'Rejected' },
  pending:  { dot: '#f59e0b', bg: '#fef3c7', label: 'Waiting' },
};

const ApproveRequests = ({ source }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [modalReq, setModalReq] = useState(null);
  const [loading, setLoading] = useState(false);

  const onModalSaved = updated => {
    setModalReq(prev => ({ ...prev, ...updated }));
    setRequests(prev => prev.map(r => r.id === updated.id && r.source === updated.source ? { ...r, ...updated } : r));
    if (selected?.id === updated.id) onCorrected(updated);
  };

  const load = () => api.get('/requests/all').then(r => {
    const all = r.data;
    setRequests(source ? all.filter(x => x.source === source) : all);
  });
  useEffect(() => { load(); }, [source]);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const approvals = selected?.approvals || [];
  const approvedCount = approvals.filter(a => a.decision === 'approved').length;
  const myTicket = approvals.find(a => (a.approver_email || '').toLowerCase() === (user?.email || '').toLowerCase());
  const canReview = selected?.status === 'pending' && (!myTicket || myTicket.decision === 'pending');
  const myDecision = myTicket?.decision || 'pending';
  const canCorrect = user?.role === 'approver' || user?.role === 'admin';

  const openRow = r => { setSelected(r); };
  const onCorrected = updated => setSelected(prev => ({ ...prev, ...updated, category: prev.category, source: prev.source, approvals: prev.approvals, comments: prev.comments }));
  const onCommented = comment => setSelected(prev => ({ ...prev, comments: [...(prev.comments || []), comment] }));

  const handleReview = async status => {
    setLoading(true);
    try {
      const roll = await api.patch(`/requests/${selected.id}/review`, { status, source: selected.source });
      const done = roll.data.approved === roll.data.total;
      toast.success(done ? `All ${roll.data.total} approvers signed off — sent to IT` : `Approved. Waiting on ${roll.data.total - roll.data.approved} more approver(s).`);
      setSelected(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Action failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={S.wrap}>
      {/* ── Table ── */}
      <div style={S.tableCard}>
        <div style={S.cardTop}>
          <div style={S.cardTitle}>
            {source === 'material_requests' ? 'Material Approvals' : source === 'vendor_requests' ? 'Vendor Approvals' : 'Approvals'}
          </div>
          <StatusFilter value={filter} onChange={setFilter} requests={requests} />
          <div style={S.countBadge}>{pendingCount} pending</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr style={S.thead}>
              {['Request #', 'Title', 'Category', 'Requested By', 'Approvals', 'Date', 'Status'].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} style={S.empty}>No requests</td></tr>}
              {filtered.map((r, i) => {
                const total = r.approvals?.length || 0;
                const done = r.approvals?.filter(a => a.decision === 'approved').length || 0;
                const active = selected?.source === r.source && selected?.id === r.id;
                return (
                  <tr key={`${r.source}-${r.id}`} onClick={() => openRow(r)}
                    style={{ ...S.tr, background: active ? '#f0f4ff' : i % 2 === 0 ? '#fff' : '#f8faff', cursor: 'pointer' }}>
                    <td style={{ ...S.td, fontWeight: 700, color: '#4f6ef7' }}>
                      <span onClick={e => { e.stopPropagation(); setModalReq(r); }} style={S.reqLink}>{r.request_number}</span>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600, color: '#0B1554' }}>{r.title}</td>
                    <td style={S.td}><span style={S.catBadge}>{r.category}</span></td>
                    <td style={S.td}>{r.requester_name}</td>
                    <td style={S.td}>
                      <span style={{ ...S.pill, ...(total && done === total ? S.pillDone : null) }}>{done}/{total || '—'}</span>
                    </td>
                    <td style={{ ...S.td, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td style={S.td}><StatusBadge status={r.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Side Panel ── */}
      {selected && (
        <div style={S.panel}>
          <div style={S.panelHead}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.panelReqNum}>{selected.request_number}</div>
              <div style={S.panelMeta}>{selected.category} · {selected.requester_name}</div>
            </div>
            <button onClick={() => setSelected(null)} style={S.closeBtn}>✕</button>
          </div>

          <div style={S.panelScroll}>
            <div style={S.strip}>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Status</span>
                <StatusBadge status={selected.status} />
              </div>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Date</span>
                <span style={S.stripVal}>{new Date(selected.created_at).toLocaleDateString()}</span>
              </div>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Approvals</span>
                 <span style={{ ...S.stripVal, color: approvedCount === approvals.length && approvals.length > 0 ? '#4f6ef7' : '#f59e0b', fontWeight: 800 }}>

                  {approvedCount}/{approvals.length}
                </span>
              </div>
            </div>

            <div style={S.section}>
              <div style={S.bigTitle}>{selected.title}</div>
              {selected.description && <div style={S.desc}>{selected.description}</div>}
              <div style={S.openModalHint}>
                Click the <strong>Request #</strong> in the table to view and edit the full form.
              </div>
            </div>

            <div style={S.section}>
              <RequestComments request={selected} canPost={canCorrect} onPosted={onCommented} />
            </div>

            {approvals.length > 0 && (
              <div style={S.section}>
                <div style={S.sectionLabel}>Approval Chain</div>
                <div style={S.progressTrack}>
                   <div style={{ ...S.progressFill, width: `${(approvedCount / approvals.length) * 100}%`, background: approvedCount === approvals.length ? '#4f6ef7' : '#4f6ef7' }} />

                </div>
                <div style={S.timeline}>
                  {approvals.map((a, i) => {
                    const ds = DECISION_STYLE[a.decision] || DECISION_STYLE.pending;
                    return (
                      <div key={i} style={S.tlRow}>
                        <div style={S.tlLeft}>
                          <div style={{ ...S.tlDot, background: ds.dot, boxShadow: `0 0 0 3px ${ds.bg}` }} />
                          {i < approvals.length - 1 && <div style={S.tlLine} />}
                        </div>
                        <div style={S.tlBody}>
                          <div style={S.tlName}>{a.approver_name}</div>
                          <div style={S.tlEmail}>{a.approver_email}</div>
                          <span style={{ ...S.tlBadge, background: ds.bg, color: ds.dot }}>{ds.label}</span>
                          {a.remarks && <div style={S.tlRemarks}>"{a.remarks}"</div>}
                          {a.decided_at && <div style={S.tlDate}>{new Date(a.decided_at).toLocaleString()}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {approvedCount === approvals.length && (
                  <div style={S.allDone}>✓ All approvers signed off — ready for IT</div>
                )}
              </div>
            )}

            {['approved', 'in_progress', 'completed'].includes(selected.status) && (
              <div style={S.section}>
                <div style={S.sectionLabel}>IT Processing</div>
                <div style={{ ...S.itBox, borderColor: selected.status === 'completed' ? '#bbf7d0' : '#fed7aa', background: selected.status === 'completed' ? '#f0fdf4' : '#fff7ed' }}>
                  <div style={S.itRow}>
                     <span style={{ ...S.itDot, background: selected.status === 'completed' ? '#4f6ef7' : selected.status === 'in_progress' ? '#f59e0b' : '#94a3b8' }} />

                    <span style={S.itStatusText}>{selected.status === 'completed' ? 'Completed' : selected.status === 'in_progress' ? 'In Progress' : 'Awaiting IT'}</span>
                  </div>
                  {selected.it_remarks && <div style={S.itRemarks}>{selected.it_remarks}</div>}
                  {selected.material_number && <div style={S.itCode}>Material #: <strong>{selected.material_number}</strong></div>}
                  {selected.business_partner_number && <div style={S.itCode}>BP #: <strong>{selected.business_partner_number}</strong></div>}
                </div>
              </div>
            )}

            {canReview ? (
              <div style={S.section}>
                <div style={S.btnRow}>
                  <button style={{ ...S.btn, ...S.btnApprove }} onClick={() => handleReview('approved')} disabled={loading}>✓ Approve</button>
                  <button style={{ ...S.btn, ...S.btnReject }} onClick={() => handleReview('rejected')} disabled={loading}>✕ Reject</button>
                </div>
              </div>
            ) : (
              <div style={S.section}>
                <div style={S.lockNote}>
                  {selected.status !== 'pending'
                    ? `This request is ${selected.status.replace('_', ' ')} — no further action needed.`
                    : `You already ${myDecision === 'approved' ? 'approved' : 'rejected'} this request.`}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {modalReq && (
        <RequestFormModal
          request={modalReq}
          onClose={() => setModalReq(null)}
          editable={canCorrect && modalReq.status === 'pending'}
          onSaved={onModalSaved}
        />
      )}
    </div>
  );
};

const S = {
  wrap: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  tableCard: { flex: 2, background: '#fff', borderRadius: 18, boxShadow: '0 4px 20px rgba(11,21,84,0.06)', overflow: 'hidden', minWidth: 0, border: '1px solid rgba(79,110,247,0.06)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '18px 24px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0B1554' },
  countBadge: { background: '#fff8e1', color: '#f59e0b', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #fde68a' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)' },
  th: { padding: '12px 18px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' },
  td: { padding: '13px 18px', fontSize: 13.5, color: '#374151' },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 14 },
  catBadge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6 },
  reqLink: { cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', color: '#4f6ef7' },
  pill: { background: '#fffbeb', color: '#b45309', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 },
  pillDone: { background: '#dcfce7', color: '#15803d' },
  panel: { width: 460, flexShrink: 0, background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(11,21,84,0.12)', overflow: 'hidden', border: '1px solid rgba(79,110,247,0.08)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  panelHead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 },
  panelReqNum: { color: '#F7D506', fontWeight: 800, fontSize: 14, marginBottom: 2 },
  panelMeta: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  closeBtn: { background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  panelScroll: { overflowY: 'auto', flex: 1 },
  strip: { display: 'flex', borderBottom: '1px solid #f1f5f9' },
  stripItem: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 16px', borderRight: '1px solid #f1f5f9' },
  stripLabel: { fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' },
  stripVal: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  section: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  sectionLabel: { fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase' },
  bigTitle: { fontSize: 14, fontWeight: 700, color: '#0B1554', marginBottom: 4, lineHeight: 1.4 },
  desc: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginBottom: 8 },
  openModalHint: { fontSize: 11.5, color: '#64748b', background: '#f8faff', border: '1px solid #eef1f7', borderRadius: 8, padding: '7px 10px', marginTop: 8 },
  progressTrack: { height: 5, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden', marginBottom: 14 },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s' },
  timeline: { display: 'flex', flexDirection: 'column' },
  tlRow: { display: 'flex', gap: 12, marginBottom: 4 },
  tlLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 },
  tlDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0, marginTop: 3 },
  tlLine: { width: 2, flex: 1, background: '#e2e8f0', margin: '4px 0' },
  tlBody: { flex: 1, paddingBottom: 12 },
  tlName: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  tlEmail: { fontSize: 11.5, color: '#64748b', marginBottom: 4 },
  tlBadge: { display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 },
  tlRemarks: { fontSize: 12, color: '#475569', fontStyle: 'italic', marginTop: 5, lineHeight: 1.5 },
  tlDate: { fontSize: 10.5, color: '#94a3b8', marginTop: 3 },
  allDone: { fontSize: 12, fontWeight: 700, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 10px', marginTop: 4 },
  itBox: { border: '1px solid', borderRadius: 10, padding: '10px 14px' },
  itRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  itDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  itStatusText: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  itRemarks: { fontSize: 12.5, color: '#374151', lineHeight: 1.5, marginBottom: 4 },
  itCode: { fontSize: 12.5, color: '#374151', marginTop: 4 },
  btnRow: { display: 'flex', gap: 8 },
  btn: { flex: 1, padding: '11px', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnApprove: { background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)', boxShadow: '0 4px 14px rgba(79,110,247,0.4)' },
  btnReject: { background: 'linear-gradient(135deg,#dc2626,#ef4444)', boxShadow: '0 4px 14px rgba(220,38,38,0.4)' },
  lockNote: { fontSize: 12.5, fontWeight: 600, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' },
};

export default ApproveRequests;
